import { BaseLayoutOptions } from '@services/MarppleShop/features/Core/service';
import { PRODUCT_LIST_SORT } from '@services/MarppleShop/features/ProductList/constant';
import { ProductListTypeS } from '@services/MarppleShop/features/ProductList/outbound/share';
import { font } from '@services/MarppleShop/shared/typography';
import { htmlIf, updateUrlParams } from '@services/MarppleShop/shared/util';
import { html, type Html, Page, View } from 'rune-ts';
import { CreatorCard, CreatorLikeToggleEvent } from '../../components/cells/CreatorCard/CreatorCard';
import { CreatorItem, CreatorItemLikeToggleEvent } from '../../components/cells/CreatorItem/CreatorItem';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { ProductCardListView } from '../../components/cells/ProductCardList/ProductCardListView.ts';
import { SearchEmptyScreen } from '@services/MarppleShop/renderApp/pages/SearchResultCreatorShop/views/SearchEmptyScreen';
import klass from './SearchResultCreatorShop.module.scss';
import { ProductCardSize } from '@services/MarppleShop/features/ProductList/type';

export interface SearchResultCreatorShopData {
  products?: ProductListTypeS.ProductCardViewData[];
}

export type SearchResultCreatorShopPageData = SearchResultCreatorShopData & {
  keyword: string;
  count: number;
  current_page?: number;
  sort: keyof typeof PRODUCT_LIST_SORT;
  layout_options: BaseLayoutOptions;
  is_mobile: boolean;
};

class SearchTitle extends View<Pick<SearchResultCreatorShopPageData, 'count' | 'keyword'>> {
  override template() {
    const { count, keyword } = this.data;

    // en
    if (ET.lang === 'en') {
      return html`<span>
        result${count > 1 ? 's' : ''} for
        <span class="${font('18_bold', '14_bold')} ${klass.pointed}">${html.preventEscape(keyword)}</span>
        search
      </span>`;
    }

    // kr, jp
    const keyword에_대한 = html`<span class="${font('18_bold', '14_bold')} ${klass.pointed}"
        >${html.preventEscape(keyword)}</span
      >${ET('mps2::search::title1')}`;
    const 검색_결과 = ET('mps2::search::title4');

    return html`${keyword에_대한} ${검색_결과}`;
  }
}

class SearchResultCreatorShopPageBodyView extends View<SearchResultCreatorShopPageData> {
  override template() {
    const { keyword, products = [], count, is_mobile } = this.data;
    return html`<div class="${klass.search_page}">
      <div class="${font('18_medium', '14_medium')} ${klass.title_container}">
        ${new SearchTitle({ count, keyword })}
      </div>

      ${htmlIf(new SearchEmptyScreen(), this.isResultEmpty())}

      <div class="${klass.section} ${htmlIf(klass.hidden, !products?.length)}">
        ${new ProductCardListView(
          { products },
          {
            resize_width: 600,
            is_lazy: true,
            container_klass: klass.product_cards_container,
            hide_badges: is_mobile,
            hide_tag: is_mobile,
            size: ProductCardSize.medium,
          },
        )}
      </div>
      ${new Pagination(
        {
          page: this.data.current_page ?? 1,
          size: is_mobile ? 5 : 10,
          total: Pagination.calcTotal({
            total_count: this.data.count,
            limit_per_page: 100,
          }),
        },
        { klass: klass.pagination },
      )}
    </div>`;
  }

  override onRender() {
    this.delegate(PaginationChangeEvent, Pagination, (e, view) => {
      const { page } = e.detail;

      location.replace(updateUrlParams(location.href, { page }));
      view.disableClick();
    });

    this.delegate(
      DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>,
      Dropdown<keyof typeof PRODUCT_LIST_SORT>,
      async (e) => {
        const sort = e.detail.option?.value;
        if (!sort) {
          throw new TypeError(`sort is required.`);
        }
        location.replace(updateUrlParams(location.href, { sort, page: 1 }));
      },
    );

    this.delegate(CreatorItemLikeToggleEvent, CreatorItem, async (_e, view) => {
      await view.toggleLikeButton();
    });

    this.delegate(CreatorLikeToggleEvent, CreatorCard, async (_e, view) => {
      await view.toggleLikeButton();
    });
  }

  private isResultEmpty(): boolean {
    return this.data.count < 1;
  }
}

export class SearchResultCreatorShopPage extends Page<SearchResultCreatorShopPageData> {
  protected override template(): Html {
    const { layout_options } = this.data;

    return html`
      <div">
        ${new LayoutView(
          {
            body: new SearchResultCreatorShopPageBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: layout_options.is_mobile,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
